import React, {useState, useEffect, useContext} from "react";
import {Link} from "react-router-dom";
import {Layout, Button, notification, DatePicker} from "antd";
import {AppContext} from "@contexts/app-context";
import {userServices, deliveriesService} from "@services";
import Error from "@components/error";
import Spinner from "@components/spinner";
import UserNotificationMessage from "@components/user-message";
import Event from "./event";
import ItemBlock from "./item-block";
import {notificationServices} from "@services/notification.services";
import SelectField from "../../select-field";
import {distributionCenterService} from "../../../services";
import moment from "moment";
import i18n from "../../../i18n";

export default function StartPage() {
  const user = userServices.getUser();

  const pageTitle = user.info.isStaff ? i18n.t('startPage.titles.stuff') : i18n.t('startPage.titles.main')
  const [dataLoaded, setDataLoaded] = useState(false);
  const [eventDataLoaded, setEventDataLoaded] = useState(false);
  const [overviewData, setOverviewData] = useState({});
  const [eventData, setEventData] = useState([]);
  const [needUpdate, setNeedUpdate] = useState(false);
  const [distCenters, setDistCenters] = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);

  const [filters, setFilters] = useState({
    from: [],
    to: [],
    dateLoadingStart: '',
    dateLoadingEnd: '',
    distributionCenters: []
  })

  if (user.isGuest()) {
    return <Error status={403} desc={i18n.t('main.status.403')}/>;
  }

  const appContext = useContext(AppContext);

  if (appContext.filter.enable) {
    appContext.disableFilter();
  }

  moment.locale(appContext.lang.locale)

  useEffect(() => {
    if (!dataLoaded || needUpdate) {
      user.info.isStaff ?
        deliveriesService
          .getLogistOverviewDeliveries(filters)
          .then((response) => {
            setOverviewData(response);

            setDataLoaded(true);
            setNeedUpdate(false);
          })
          .catch((err) => {
            notification.error({
              message: i18n.t(`startPage.notification.info.title`),
              description: err,
            });
            setNeedUpdate(false);
          }) :
        deliveriesService
          .getCarrierOverviewDeliveries()
          .then((response) => {
            setOverviewData(response);

            setDataLoaded(true);
            setNeedUpdate(false);
          })
          .catch((err) => {
            notification.error({
              message: i18n.t(`startPage.notification.info.title`),
              description: err,
            });
            setNeedUpdate(false);
          });
    }
  }, [dataLoaded, needUpdate]);

  useEffect(() => {
    if (!eventDataLoaded) {
      notificationServices
        .getNotificationList()
        .then((response) => {
          setEventData(response.filter((item) => item.DateRead === "Не прочитано").slice(0, 3));

          setEventDataLoaded(true);
        })
        .catch((err) => {
          notification.error({
            message: i18n.t(`startPage.notification.events.title`),
            description: err,
          });
        });
    }
  }, [eventDataLoaded]);

  useEffect(() => {
    if (!companiesLoaded && user.info.role !== 'company_person') {
      distributionCenterService.getCompanies()
        .then((response) => {
          setDistCenters(response);
          setCompaniesLoaded(true);
        }).catch((err) => {
        notification.error({
          message: i18n.t(`startPage.notification.events.title`),
          description: err,
        });
        setCompaniesLoaded(true);
      });
    }
  }, [])

  return (
    <Layout style={{marginBottom: "100px"}}>
      <Layout.Content>
        <Spinner loading={!dataLoaded} label={i18n.t('main.loadText.loading')}>
          <UserNotificationMessage/>
          <h2 className="title title--light">{pageTitle}</h2>
          <div className="content-wrapper">
            {user.info.isStaff &&
              <div className='content-filter'>
                <div className="content-filter__item">
                  <SelectField
                    name="distributionCenters"
                    selectedValue={filters.distributionCenters}
                    values={distCenters}
                    onChange={(value) => {
                      setFilters({
                        ...filters,
                        distributionCenters: value
                      });
                    }}
                    notFoundText={i18n.t('startPage.filter.distributionCenters.empty')}
                    placeholder={i18n.t('startPage.filter.distributionCenters.placeholder')}
                    getErrors={() => {
                    }}
                    style={{width: "100%"}}
                    mode={'multiple'}
                  />
                </div>
                <div className="content-filter__item content-filter__item--buttons">
                  <Button
                    className="content-filter__button"
                          type="primary"
                          size="default"
                          icon="search"
                          htmlType="button"
                          title={i18n.t('startPage.filter.buttons.submit')}
                          onClick={() => setNeedUpdate(true)}
                  />
                  <Button
                    className="content-filter__button"
                          type="secondary"
                          size="default"
                          icon="close"
                          htmlType="button"
                          title={i18n.t('startPage.filter.buttons.clear')}
                          onClick={() => {
                            setFilters({
                              from: [],
                              to: [],
                              dateLoadingStart: '',
                              dateLoadingEnd: '',
                              distributionCenters: []
                            });
                            setNeedUpdate(true);
                          }}
                  />
                </div>
              </div>
            }
            <div className="content-item">
              <h3 className="content-item__title">{
                user.info.isStaff ?
                  i18n.t('startPage.content.column1.title.stuff') :
                  i18n.t('startPage.content.column1.title.main')
              }</h3>
              {overviewData && !!overviewData.free &&
                overviewData.free.map((item, index) => <ItemBlock data={item} key={item.id} type={"free"}
                                                                  user={user}
                                                                  setNeedUpdateDeliveryList={setNeedUpdate}/>)}
            </div>
            <div className="content-item">
              <h3 className="content-item__title">{
                user.info.isStaff ?
                  i18n.t('startPage.content.column2.title.stuff') :
                  i18n.t('startPage.content.column2.title.main')
              }</h3>
              {overviewData && !!overviewData.onFilling &&
                overviewData.onFilling.map((item, index) => <ItemBlock data={item} key={item.id} type={"onFilling"}
                                                                       user={user}
                                                                       setNeedUpdateDeliveryList={setNeedUpdate}/>)}
            </div>
            <div className="content-item">
              <h3 className="content-item__title">{
                user.info.isStaff ?
                  i18n.t('startPage.content.column3.title.stuff') :
                  i18n.t('startPage.content.column3.title.main')
              }</h3>
              {user.info.isStaff ?
                overviewData && !!overviewData.awaitingDocs &&
                overviewData.awaitingDocs.map((item, index) => <ItemBlock data={item} key={item.id}
                                                                          type={"awaitingDocs"}
                                                                          user={user}
                                                                          setNeedUpdateDeliveryList={setNeedUpdate}/>) :
                overviewData && !!overviewData.onLoading &&
                overviewData.onLoading.map((item, index) => <ItemBlock data={item} key={item.id} type={"onLoading"}
                                                                       user={user}
                                                                       setNeedUpdateDeliveryList={setNeedUpdate}/>)
              }
            </div>
            <div className="content-item">
              <h3 className="content-item__title">{
                user.info.isStaff ?
                  i18n.t('startPage.content.column4.title.stuff') :
                  i18n.t('startPage.content.column4.title.main')
              }</h3>
              {user.info.isStaff &&
                <div className="content-filter-item">
                  <DatePicker
                    style={{width: "100%"}}
                    defaultValue={filters.dateLoadingStart !== '' ? moment(filters.dateLoadingStart, 'DD.MM.YYYY') : null}
                    format="DD.MM.YYYY"
                    size="normal"
                    name="dateLoadingStart"
                    placeholder={i18n.t('startPage.content.column4.filter.date.placeholder')}
                    allowClear={false}
                    onChange={(value) => {
                      setFilters({
                        ...filters,
                        dateLoadingStart: value ? moment(value).format("DD.MM.YYYY") : null,
                        dateLoadingEnd: value ? moment(value).format("DD.MM.YYYY") : null,
                      });
                      setNeedUpdate(true);
                    }}
                  />
                </div>
              }
              {user.info.isStaff ?
                overviewData && !!overviewData.onLoading &&
                overviewData.onLoading.map((item, index) => <ItemBlock data={item} key={item.id} type={"onLoading"}
                                                                       user={user}
                                                                       setNeedUpdateDeliveryList={setNeedUpdate}/>) :
                overviewData && !!overviewData.enRoute &&
                overviewData.enRoute.map((item, index) => <ItemBlock data={item} key={item.id} type={"enRoute"}
                                                                     user={user}
                                                                     setNeedUpdateDeliveryList={setNeedUpdate}/>)}
            </div>
          </div>
        </Spinner>
        <Spinner loading={!eventDataLoaded} label={i18n.t('startPage.events.loading')}>
          <h2 className="title title--light">{i18n.t('startPage.events.title')}</h2>
          <div className="content-wrapper content-wrapper--mono">
            {!!eventData.length && eventData.map((event) => {
              return <div className="content-item" key={event.Id}>
                <Event date={event.DateCreated} text={event.Message}/>
              </div>
            })}
            {!eventData.length &&
              <div className="content-item">
                <p className="content-item__title">{i18n.t('startPage.events.empty')}</p>
              </div>
            }
            <Link className="btn typical-btn typical-btn-green"
                  to={user.info.isStaff ? `/consignor/user-notification/` : `/company/user-notification/`}
                  style={{width: "auto", height: "auto", padding: "0 8px"}}>{i18n.t('startPage.events.buttons.allEvents')}</Link>
          </div>
        </Spinner>
      </Layout.Content>
    </Layout>
  )
}
